import axios, { AxiosResponse } from 'axios'
import {
  ConselhoDtoCreate,
  ConselhoDtoUpdate,
  ConselhoList,
  ConselhoView,
  FuncionarioDtoEspecialidades,
  FuncionarioDtoEspecialidadesAdd,
  FuncionarioDtoEspecialidadesRemove,
  MedicoDtoCreate,
  MedicoDtoStatus,
  MedicoDtoUpdate,
} from './_models'
import { PessoasQueryResponse } from 'app/modules/auth'
import { FuncionarioView } from 'app/modules/apps/funcionarios/funcionarios-list/core/_models'
import { EnderecoList } from 'app/modules/apps/empresa/empresas-list/core/_models'

const API_URL = process.env.REACT_APP_API_URL

const CONSELHOS_URL = `${API_URL}/conselhos`

/**
 * Busca pessoas que tem cargo de profissional de saúde
 * @param query string
 * @returns PessoaList
 */
export const getProfisionaisDeSaude = (query: string): Promise<PessoasQueryResponse> => {
  return axios
    .get(`${API_URL}/funcionarios/medicos?${query}`)
    .then((d: AxiosResponse<PessoasQueryResponse>) => d.data)
}

/**
 * Busca pessoas que tem cargo de profissional de saúde em empresas com perfil responsável
 * @param query string
 * @returns PessoaList
 */
export const getProfisionaisDeSaudeAdmin = (query: string): Promise<PessoasQueryResponse> => {
  return axios
    .get(`${API_URL}/funcionarios/admin/medicos?${query}`)
    .then((d: AxiosResponse<PessoasQueryResponse>) => d.data)
}

/**
 * Lista especialidades dos vínculos de funcionário da pessoa
 * @param pessoaId Identificador de pessoa
 * @returns FuncionarioDtoEspecialidades
 */
export const getProfissionalVinculo = (pessoaId: number): Promise<FuncionarioDtoEspecialidades> => {
  return axios
    .get(`${API_URL}/funcionarios/especialidades/${pessoaId}`)
    .then((d: AxiosResponse<FuncionarioDtoEspecialidades>) => d.data)
}

/**
 * Adiciona especialidades aos vínculos de funcionário da pessoa
 * @param vinculo FuncionarioDtoEspecialidadesAdd
 * @returns FuncionarioDtoEspecialidades
 */
export const createVinculoEspecialidade = (
  vinculo: FuncionarioDtoEspecialidadesAdd
): Promise<FuncionarioDtoEspecialidades> => {
  return axios
    .post(`${API_URL}/funcionarios/especialidades`, vinculo)
    .then((d: AxiosResponse<FuncionarioDtoEspecialidades>) => d.data)
}

/**
 * Registra funcionário em múltiplas empresas
 * @param profissional MedicoDtoCreate
 * @returns FuncionarioView[]
 */
export const createProfissionalDeSaude = (
  profissional: MedicoDtoCreate
): Promise<FuncionarioView> => {
  return axios
    .post(`${API_URL}/funcionarios`, profissional)
    .then((resp: AxiosResponse<FuncionarioView>) => resp.data)
}

/**
 * Altera registro de funcionário em múltiplas empresas
 * @param profissional MedicoDtoUpdate
 * @returns FuncionarioDtoEspecialidades
 */
export const updateProfissionalDeSaude = (
  profissional: MedicoDtoUpdate
): Promise<FuncionarioDtoEspecialidades> => {
  return axios
    .put(`${API_URL}/funcionarios`, profissional)
    .then((resp: AxiosResponse<FuncionarioDtoEspecialidades>) => resp.data)
}

/**
 * Busca endereços do funcionário
 * @param funcionarioId Identificador de funcionário
 * @returns EnderecoList[]
 */
export const getEnderecoByFuncionario = (funcionarioId: number): Promise<EnderecoList[]> => {
  return axios
    .get(`${API_URL}/funcionarios/endereco/${funcionarioId}`)
    .then((resp: AxiosResponse<EnderecoList[]>) => resp.data)
}

/**
 * Remove especialidades dos vínculos de funcionário da pessoa
 * @param funcionarioRemove FuncionarioDtoEspecialidadesRemove
 * Se informar apenas empresa, desvincula a todas as especialidades da pessoa na empresa.
 * Se informar apenas especialidades, desvincula as especialidades de todos os vínculos da pessoa.
 * Se informar apenas pessoa, desvincula todas as especialidades de todos os vínculos da pessoa.
 * @returns FuncionarioDtoEspecialidades
 */
export const removeEspecialidadeOfVinculo = (
  funcionarioRemove: FuncionarioDtoEspecialidadesRemove
): Promise<FuncionarioDtoEspecialidades> => {
  return axios
    .delete(`${API_URL}/funcionarios/especialidades`, { data: funcionarioRemove })
    .then((resp: AxiosResponse<FuncionarioDtoEspecialidades>) => resp.data)
}

/**
 * Altera status de funcionárop em múltiplas empresas
 * @param data MedicoDtoStatus
 * @returns FuncionarioView[]
 */
export const changeStatusOfEmpresas = (data: MedicoDtoStatus): Promise<FuncionarioView[]> => {
  return axios
    .patch(`${API_URL}/funcionarios/status`, data)
    .then((resp: AxiosResponse<FuncionarioView[]>) => resp.data)
}

/**
 * Busca todos os conselhos
 * @returns ConselhoList[]
 */
export const getConselhos = (): Promise<ConselhoList[]> => {
  return axios.get(CONSELHOS_URL).then((resp: AxiosResponse<ConselhoList[]>) => resp.data)
}

/**
 * Busca um conselho
 * @param conselhoId Identificador do conselho
 * @returns ConselhoView
 */
export const getConselhoById = (conselhoId: number): Promise<ConselhoView> => {
  return axios
    .get(`${CONSELHOS_URL}/${conselhoId}`)
    .then((resp: AxiosResponse<ConselhoView>) => resp.data)
}

/**
 * Cadastra um conselho
 * @param data ConselhoDtoCreate
 * @returns ConselhoView
 */
export const createConselho = (data: ConselhoDtoCreate): Promise<ConselhoView> => {
  return axios.post(CONSELHOS_URL, data).then((resp: AxiosResponse<ConselhoView>) => resp.data)
}

/**
 * Altera um conselho
 * @param data ConselhoDtoUpdate
 * @returns ConselhoView
 */
export const updateConselho = (data: ConselhoDtoUpdate): Promise<ConselhoView> => {
  return axios.put(CONSELHOS_URL, data).then((resp: AxiosResponse<ConselhoView>) => resp.data)
}

/**
 * Remove um conselho
 * @param conselhoId Identificador do conselho
 * @returns void
 */
export const deleteConselho = (conselhoId: number): Promise<void> => {
  return axios
    .delete(`${CONSELHOS_URL}/${conselhoId}`)
    .then((resp: AxiosResponse<void>) => resp.data)
}
